import React, { useEffect, useState } from "react";
import { Download } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { API_BASE } from "../lib/api";
import PageLoader from "../components/PageLoader";

const ResourceModal = ({ resource, onClose }) => {
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    organisation: "",
    role: "",
    consent: false,
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) {
      toast.error("Please confirm consent to proceed.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/resources/${resource.id}/request`, form);
      const url = `${API_BASE}/resources/${resource.id}/download-token/${data.download_token}`;
      window.open(url, "_blank", "noopener,noreferrer");
      toast.success("Your download is ready");
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Download request failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center px-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      data-testid="resource-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white max-w-lg w-full p-8 lg:p-10"
      >
        <div className="ek-eyebrow text-[#7737F6]">{resource.category}</div>
        <h3 className="ek-heading text-2xl mt-3">{resource.title}</h3>
        <p className="ek-body text-[14.5px] mt-3">{resource.description}</p>

        <form onSubmit={submit} className="mt-6 space-y-3">
          <input
            type="text"
            required
            placeholder="Full name"
            value={form.full_name}
            onChange={(e) => setForm({ ...form, full_name: e.target.value })}
            className="w-full border border-zinc-300 px-3 py-2 text-[16px]"
            data-testid="resource-full-name"
          />
          <input
            type="email"
            required
            placeholder="Work email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="w-full border border-zinc-300 px-3 py-2 text-[16px]"
            data-testid="resource-email"
          />
          <input
            type="text"
            placeholder="Organisation (optional)"
            value={form.organisation}
            onChange={(e) => setForm({ ...form, organisation: e.target.value })}
            className="w-full border border-zinc-300 px-3 py-2 text-[16px]"
            data-testid="resource-org"
          />
          <input
            type="text"
            placeholder="Role (optional)"
            value={form.role}
            onChange={(e) => setForm({ ...form, role: e.target.value })}
            className="w-full border border-zinc-300 px-3 py-2 text-[16px]"
            data-testid="resource-role"
          />
          <label className="flex items-start gap-2 text-[13px] text-zinc-600 mt-2">
            <input
              type="checkbox"
              checked={form.consent}
              onChange={(e) => setForm({ ...form, consent: e.target.checked })}
              data-testid="resource-consent"
              className="mt-1"
            />
            <span>I agree to Ekselens contacting me about this content and related services. See our Privacy Policy.</span>
          </label>
          <div className="flex gap-3 mt-5">
            <button
              type="submit"
              disabled={busy}
              className="ek-btn ek-btn-primary disabled:opacity-60"
              data-testid="resource-submit"
            >
              {busy ? "Preparing…" : "Get download"}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="ek-btn ek-btn-outline"
              data-testid="resource-cancel"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const Resources = () => {
  const [resources, setResources] = useState([]);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/resources");
        setResources(data || []);
      } catch (e) {
        // no toast — show empty state instead
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleDownload = (r) => {
    if (r.gated) {
      setActive(r);
      return;
    }
    window.open(`${API_BASE}/resources/${r.id}/download`, "_blank", "noopener,noreferrer");
  };

  return (
    <>
      <section
        className="ek-gradient-dark text-white relative overflow-hidden"
        data-testid="resources-hero"
      >
        <div className="ek-star-watermark" />
        <div className="ek-vignette-top" />
        <div className="ek-container relative z-10 py-20 md:py-28">
          <div className="ek-eyebrow text-[#A179FA]">Resources</div>
          <h1 className="ek-heading text-4xl md:text-5xl lg:text-6xl mt-4 text-white max-w-4xl leading-tight">
            Practical guides, frameworks and downloadable templates.
          </h1>
          <p className="text-zinc-200 font-light text-lg md:text-xl leading-relaxed mt-8 max-w-3xl">
            Curated content from Ekselens — short-form playbooks, lifecycle diagnostics and reference frameworks across transformation, AI adoption, supplier optimisation, delivery confidence and benefits realisation.
          </p>
        </div>
      </section>

      <section className="bg-[#F2F2F3] ek-section" data-testid="resources-list">
        <div className="ek-container">
          {/* Featured self-assessment lead-gen cards */}
          <div className="mb-10">
            <Link to="/system-of-proof" data-testid="featured-system-of-proof"
              className="block bg-[#0B0B0C] text-white p-7 md:p-9 mb-8 group hover:bg-[#16161A] transition-colors border border-[#7737F6]/40">
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <span className="inline-flex items-center px-2.5 py-1 text-[10px] font-bold tracking-[0.18em] text-white bg-[#7737F6] uppercase">System of proof</span>
                <span className="text-[10px] font-semibold tracking-[0.22em] text-[#A179FA] uppercase">Reinvention assurance · flagship</span>
              </div>
              <h3 className="ek-heading text-2xl md:text-3xl text-white leading-tight">How defensible is your enterprise's system of proof?</h3>
              <p className="text-zinc-300 text-[14px] mt-3 font-light max-w-3xl">The Evidence Graph Maturity diagnostic rates your evidence trail across five pillars — Provenance, Challenge, Continuity, Control and Disclosure. 25 statements, an honest radar, and the two pillars we'd start with. No login required.</p>
              <span className="inline-flex items-center gap-1 text-[#A179FA] text-[13px] mt-4 group-hover:gap-2 transition-all">Start the assessment →</span>
            </Link>
            <Link to="/system-of-proof-library" data-testid="featured-framework-library"
              className="block border border-zinc-200 p-5 mb-8 group hover:border-[#7737F6]/50 transition-colors">
              <div className="ek-eyebrow text-[#7737F6] mb-1">Doctrine library</div>
              <h3 className="ek-heading text-lg text-black leading-tight">Download the frameworks — Manifesto, EGMM &amp; MRM</h3>
              <p className="text-zinc-600 text-[13px] mt-2">The System of Proof doctrine behind our work, free to download.</p>
              <span className="inline-flex items-center gap-1 text-[#7737F6] text-[13px] mt-3 group-hover:gap-2 transition-all">Open the library →</span>
            </Link>
            <div className="ek-eyebrow text-[#7737F6] mb-3">EKSELENS360 self-assessments</div>
            <h2 className="ek-heading text-2xl md:text-3xl text-black mb-6 leading-tight">Twelve free maturity diagnostics across the EKSELENS360 portfolio.</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-zinc-200">
            {[
              { slug: "itsm360", brand: "ITSM360", h: "Where is your IT service operation today?", q: 7, starter: false },
              { slug: "ai360", brand: "AI360", h: "Where is your AI readiness today?", q: 6, starter: false },
              { slug: "data360", brand: "DATA360", h: "Where is your data & analytics capability today?", q: 7, starter: false },
              { slug: "pmo360", brand: "PMO360", h: "How disciplined is your PMO operation?", q: 5, starter: true },
              { slug: "change360", brand: "CHANGE360", h: "How well does change stick in your organisation?", q: 5, starter: true },
              { slug: "digital360", brand: "DIGITAL360", h: "How mature is your digital transformation?", q: 5, starter: true },
              { slug: "operating360", brand: "OPERATING360", h: "Is your operating model delivering the strategy?", q: 5, starter: true },
              { slug: "supplier360", brand: "SUPPLIER360", h: "Are you extracting value from your supplier estate?", q: 5, starter: true },
              { slug: "benefits360", brand: "BENEFITS360", h: "How credible is your benefits-realisation discipline?", q: 5, starter: true },
              { slug: "govern360", brand: "GOVERN360", h: "Is your governance a control function — or ceremony?", q: 5, starter: true },
              { slug: "capability360", brand: "CAPABILITY360", h: "Do you know where your capability gaps are?", q: 5, starter: true },
              { slug: "stakeholder360", brand: "STAKEHOLDER360", h: "How well does your organisation listen — and respond?", q: 5, starter: true },
            ].map((sa) => (
              <Link
                key={sa.slug}
                to={`/resources/${sa.slug}-self-assessment`}
                className="block bg-[#0B0B0C] text-white p-6 md:p-7 group hover:bg-[#16161A] transition-colors"
                data-testid={`featured-${sa.slug}-self-assessment`}
              >
                <div className="flex items-center gap-2 mb-3 flex-wrap">
                  <span className="inline-flex items-center px-2.5 py-1 text-[10px] font-bold tracking-[0.18em] text-white bg-[#7737F6] uppercase">{sa.brand}</span>
                  {sa.starter ? (
                    <span className="inline-flex items-center px-2 py-1 text-[9px] font-bold tracking-[0.18em] text-[#A179FA] border border-[#7737F6]/40 uppercase">Starter v0.9</span>
                  ) : null}
                </div>
                <div className="text-[10px] font-semibold tracking-[0.22em] text-[#A179FA] uppercase mb-2">Self-assessment · {sa.q} questions · 3 min</div>
                <h3 className="ek-heading text-lg md:text-xl text-white leading-tight">{sa.h}</h3>
                <p className="text-zinc-300 text-[13px] mt-3 font-light">Instant maturity radar. Top three improvement moves. No login required.</p>
                <span className="inline-flex items-center gap-1 text-[#A179FA] text-[12.5px] mt-4 group-hover:gap-2 transition-all">Start now →</span>
              </Link>
            ))}
            </div>
          </div>

          {loading ? (
            <PageLoader testid="resources-loading" />
          ) : resources.length === 0 ? (
            <div className="bg-white p-10 text-center border border-zinc-200" data-testid="resources-empty">
              <h2 className="ek-heading text-2xl text-black">New resources coming soon.</h2>
              <p className="ek-body text-[15px] mt-4 max-w-xl mx-auto">
                We're curating a library of downloadable guides and frameworks. In the meantime, the brochures linked from each service page contain a focused written summary you can share with your team.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-zinc-200">
              {resources.map((r) => (
                <article
                  key={r.id}
                  className="bg-white p-8 lg:p-10 flex flex-col"
                  data-testid={`resource-card-${r.id}`}
                >
                  <div className="text-[11px] font-semibold tracking-[0.18em] text-[#7737F6] uppercase">{r.category}</div>
                  <h3 className="ek-heading text-xl md:text-2xl mt-3 text-black leading-tight">{r.title}</h3>
                  <p className="ek-body text-[14.5px] mt-4 flex-1">{r.description}</p>
                  <div className="text-[12px] text-zinc-500 mt-5">
                    {r.filename} · {Math.round(r.size_bytes / 1024)} KB
                  </div>
                  <button
                    type="button"
                    onClick={() => handleDownload(r)}
                    className="ek-btn ek-btn-primary mt-5"
                    data-testid={`download-resource-${r.id}`}
                  >
                    <Download size={16} />
                    {r.gated ? "Request download" : "Download"}
                  </button>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      {active ? <ResourceModal resource={active} onClose={() => setActive(null)} /> : null}
    </>
  );
};

export default Resources;
